import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Table } from "react-bootstrap";
import { FaPen, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getFullName } from "../../../../utils/helpers";
import { getProfilePictureUrl } from "../../../../utils/imageUtils";
import { fetchRoles } from "../../../../redux/slices/rolesSlice";

const AVATAR_PALETTE = [
    '#F87171', '#FB923C', '#FBBF24', '#34D399',
    '#22D3EE', '#60A5FA', '#818CF8', '#C084FC',
    '#F472B6', '#4ADE80', '#38BDF8', '#FCA5A5'
];

const getAvatarColor = (user) => {
    const name = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
    const code = name ? name.toUpperCase().charCodeAt(0) : 0;
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString.slice(0, 19).replace("T", " ");
};

const MEMBERS_PER_PAGE = 10;

const SectionHeading = ({ children, aside }) => (
    <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
            <div style={{ width: '4px', height: '20px', backgroundColor: 'var(--brand-primary)', borderRadius: '2px' }} />
            <h5 className="m-0 txt-main fw-bold">{children}</h5>
        </div>
        {aside}
    </div>
);

const ViewTeamModal = ({ team, onEdit, onClose }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const dispatch = useDispatch();

    const roles = useSelector((state) => state.roles.items);
    const allUsers = useSelector((state) => state.users.items);

    const allMembers = team?.memberships || team?.members || [];
    const members = allMembers.filter(m => !m.left_at);

    const totalPages = Math.max(1, Math.ceil(members.length / MEMBERS_PER_PAGE));
    const paginatedMembers = useMemo(
        () => members.slice((page - 1) * MEMBERS_PER_PAGE, page * MEMBERS_PER_PAGE),
        [members, page]
    );

    useEffect(() => {
        dispatch(fetchRoles());
    }, [dispatch]);

    if (!team) return null;

    return (
        <Modal show={!!team} onHide={onClose} centered size="lg" contentClassName="modal-content">
            <Modal.Header closeButton className="border-bottom-0 pb-0">
                <Modal.Title className="fw-bold txt-main">{t("components.viewTeamModal.title")}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="d-flex flex-wrap gap-4 mb-4">
                    <div>
                        <small className="txt-subtle fw-bold text-uppercase d-block mb-1" style={{ fontSize: 'var(--text-small)' }}>
                            {t("components.viewTeamModal.fields.name")}
                        </small>
                        <h5 className="fw-bold txt-main m-0">{team.name}</h5>
                    </div>
                    <div>
                        <small className="txt-subtle fw-bold text-uppercase d-block mb-1" style={{ fontSize: 'var(--text-small)' }}>
                            {t("components.viewTeamModal.fields.description")}
                        </small>
                        <span className="txt-main">{team.description || t("components.viewTeamModal.placeholders.noDescription")}</span>
                    </div>
                    <div>
                        <small className="txt-subtle fw-bold text-uppercase d-block mb-1" style={{ fontSize: 'var(--text-small)' }}>
                            {t("components.viewTeamModal.fields.createdAt")}
                        </small>
                        <span className="txt-main">{formatDate(team.created_at)}</span>
                    </div>
                </div>

                <SectionHeading
                    aside={
                        <span className="txt-subtle" style={{ fontSize: 'var(--text-small)' }}>
                            {t("components.viewTeamModal.labels.memberCount", { count: members.length })}
                        </span>
                    }
                >
                    {t("components.viewTeamModal.sections.members")}
                </SectionHeading>

                <div
                    className="atlar-card p-3"
                    style={{ backgroundColor: 'var(--gray-50)', border: 'var(--border-thin)', borderRadius: 'var(--border-radius)' }}
                >
                    {members.length > 0 ? (
                        <>
                            <Table className="mb-0 align-middle" borderless>
                                <thead>
                                    <tr>
                                        <th className="txt-subtle fw-semibold" style={{ fontSize: 'var(--text-small)' }}>{t("components.viewTeamModal.table.name")}</th>
                                        <th className="txt-subtle fw-semibold" style={{ fontSize: 'var(--text-small)' }}>{t("components.viewTeamModal.table.role")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedMembers.map((member, index) => {
                                        const userId = member.user?.id || member.user_id;
                                        const fullUser = allUsers.find(u => u.id === userId) || member.user || {};

                                        const roleId = fullUser.role_id || fullUser.role?.id;
                                        const matchedRole = roles.find(r => r.id === roleId);
                                        const roleName = fullUser.role?.name || matchedRole?.name || (typeof fullUser.role === 'string' ? fullUser.role : "N/A");

                                        const pic = getProfilePictureUrl(fullUser);

                                        return (
                                            <tr key={index}>
                                                <td className="fw-semibold txt-main">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {pic ? (
                                                            <img
                                                                src={pic}
                                                                alt={t("viewTeamModal.alt.profilePic", { name: getFullName(fullUser) })}
                                                                className="rounded-circle shadow-sm"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                                                style={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: getAvatarColor(fullUser),
                                                                    color: 'var(--white)',
                                                                    fontSize: 'var(--text-base)'
                                                                }}
                                                            >
                                                                {getFullName(fullUser).trim().charAt(0).toUpperCase() || '?'}
                                                            </div>
                                                        )}
                                                        {getFullName(fullUser)}
                                                    </div>
                                                </td>
                                                <td className="fw-semibold txt-main">{roleName}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: 'var(--border-thin)' }}>
                                    <Button
                                        disabled={page === 1}
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                    >
                                        <FaChevronLeft size={12} />
                                    </Button>

                                    <span className="txt-subtle fw-semibold" style={{ fontSize: 'var(--text-small)' }}>
                                        {page} / {totalPages}
                                    </span>

                                    <Button
                                        disabled={page >= totalPages}
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    >
                                        <FaChevronRight size={12} />
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="txt-subtle m-0">{t("components.viewTeamModal.emptyState")}</p>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="border-top-0">
                <Button
                    className="fw-semibold d-flex align-items-center gap-2"
                    style={{
                        backgroundColor: 'var(--bg-surface)',
                        color: 'var(--text-primary)',
                        border: 'var(--border-thin)',
                        borderRadius: 'var(--btn-radius)'
                    }}
                    onClick={onEdit}
                >
                    <FaPen size={12} /> {t("components.viewTeamModal.buttons.edit")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewTeamModal;